import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import userImage from "../../assets/images/userImage.png";
import FetchData, { GetListAsync, GetOne } from "../../providers/dataProvider";
import { formatDate } from "../../utils/Validate";

const getFactoryReleaseId = (item) =>
    item?.aggregateId ||
    item?.factoryReleaseId ||
    item?.factoryOrderId ||
    item?.id;

const getPendingList = (result) => {
    if (Array.isArray(result?.data)) return result.data;
    if (Array.isArray(result?.data?.payload)) return result.data.payload;
    return [];
};

const asArray = (value) => {
    if (Array.isArray(value?.payload)) return value.payload;
    if (Array.isArray(value?.data)) return value.data;
    if (Array.isArray(value)) return value;
    return [];
};

const getRelatedOrder = (factoryOrder, orderDataList) => {
    const orders = asArray(orderDataList);
    const customerOrderId = factoryOrder?.customerOrderId || factoryOrder?.orderId;
    const customerOrderReference = factoryOrder?.customerOrderReference || factoryOrder?.orderReference;
    const hasCustomerOrderId = customerOrderId !== null && customerOrderId !== undefined && customerOrderId !== "";

    return orders.find((order) =>
        (
            hasCustomerOrderId &&
            (
                String(order?.id) === String(customerOrderId) ||
                String(order?.customerOrderId) === String(customerOrderId) ||
                String(order?.orderId) === String(customerOrderId)
            )
        ) ||
        (
            customerOrderReference &&
            String(order?.customerOrderReference || order?.orderReference) === String(customerOrderReference)
        )
    );
};

const FactoryApprovalNotification = ({ onApprovalCountChange }) => {
    const navigate = useNavigate();
    const [pendingApprovals, setPendingApprovals] = useState([]);
    const [loading, setLoading] = useState(false);
    const { data: orderDataList } = FetchData("order", {
        pageNumber: 1,
        pageSize: 100,
    });
 const { data: projectData } = FetchData("project");
  const { data: customerData } = FetchData("Customer");

    const cleanupModalBackdrop = () => {
        document.querySelectorAll(".modal-backdrop").forEach((backdrop) => backdrop.remove());
        document.body.classList.remove("modal-open");
        document.body.style.removeProperty("padding-right");
    };

    const closeApprovalDrawer = () => {
        const $ = window.jQuery || window.$;
        const modal = window.document.getElementById("factoryApprovalRightSideModalPopup");
        if ($ && modal) {
            $(modal).modal("hide");
            $(document).off("focusin.bs.modal");
        } else if (modal) {
            modal.classList.remove("show");
            modal.style.display = "none";
            modal.setAttribute("aria-hidden", "true");
            modal.removeAttribute("aria-modal");
        }
        cleanupModalBackdrop();
    };

    const fetchFactoryOrderDetails = async (approvalItem) => {
        const factoryReleaseId = getFactoryReleaseId(approvalItem);
        if (!factoryReleaseId) return approvalItem;

        const result = await new Promise((resolve) => {
            GetOne("factoryOrder", { id: factoryReleaseId }, (response) => resolve(response));
        });

        return {
            ...approvalItem,
            factoryOrder: result?.message === "success" ? result?.data : null,
        };
    };

    const fetchPendingApprovals = async () => {
        setLoading(true);
        try {
            const result = await GetListAsync("factoryApprovalPending", {});
            const rawList = getPendingList(result);
            const enrichedList = await Promise.all(rawList.map(fetchFactoryOrderDetails));

            setPendingApprovals(enrichedList);
            onApprovalCountChange?.(enrichedList.length);
        } catch {
            setPendingApprovals([]);
            onApprovalCountChange?.(0);
        } finally {
            setLoading(false);
        }
    };

    const handleApprovalClick = (approval) => {
        closeApprovalDrawer();
        navigate("/factory-order-approval-view", {
            state: {
                approval,
                id: getFactoryReleaseId(approval),
            },
        });
    };

    useEffect(() => {
        fetchPendingApprovals();

        const handleApprovalDone = () => fetchPendingApprovals();
        window.addEventListener("approvalStatusChanged", handleApprovalDone);
        return () => window.removeEventListener("approvalStatusChanged", handleApprovalDone);
    }, []);

    useEffect(() => {
        const modal = window.document.getElementById("factoryApprovalRightSideModalPopup");
        if (!modal) return undefined;

        modal.addEventListener("show.bs.modal", fetchPendingApprovals);
        modal.addEventListener("hidden.bs.modal", cleanupModalBackdrop);

        return () => {
            modal.removeEventListener("show.bs.modal", fetchPendingApprovals);
            modal.removeEventListener("hidden.bs.modal", cleanupModalBackdrop);
        };
    }, []);

    const handleViewAll = () => {
    closeApprovalDrawer();
    navigate("/factory-order-approval");
};

    return (
        <div
            className="modal fade drawer right-align"
            id="factoryApprovalRightSideModalPopup"
            tabIndex="-1"
            role="dialog"
            aria-labelledby="factoryApprovalRightSideModalPopupLabel"
            data-backdrop="false"
        >
            <div className="modal-dialog modal-sm" role="document">
                <div className="modal-content">
                    <div className="header notification-drawer-header">
                        <div className="d-flex justify-content-between">
                            <h5 id="factoryApprovalRightSideModalPopupLabel">Pending Factory Approvals</h5>
                            <button
                                className="notification-close-btn"
                                type="button"
                                data-dismiss="modal"
                                onClick={closeApprovalDrawer}
                                aria-label="Close approvals"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    </div>
                    <div className="modal-body notification-drawer-body approval-drawer-body">
                        <div className="approval-list">
                            {loading && (
                                <div className="notification-empty">
                                    <p>Loading approvals...</p>
                                </div>
                            )}

                            {!loading && !pendingApprovals?.length && (
                                <div className="notification-empty">
                                    <p>No pending approvals</p>
                                </div>
                            )}

                            {!loading && pendingApprovals?.map((approval, index) => {
                                const factoryOrder = approval?.factoryOrder || {};
                                const referenceNo = approval?.referenceNo || factoryOrder?.factoryReleaseReference || "-";
                                const relatedOrder = getRelatedOrder(factoryOrder, orderDataList);
                                 const customerDetails = customerData?.find(customer => customer.id === relatedOrder?.customerId);

                                const projectDetails = projectData?.find(project => project.id === relatedOrder?.projectId);
                                const projectName =
                                    customerDetails?.customerName ||
                                    factoryOrder?.projectName ||
                                    approval?.projectName ||
                                    "-";

                                return (
                                    <div className="approval-list-item" key={approval?.approvalRequestId || approval?.aggregateId || index}>
                                        <div
                                            className="unread-message-area notification-card approval-notification-card"
                                            onClick={() => handleApprovalClick(approval)}
                                        >
                                            <div className="message-content-area">
                                                <div className="notification-avatar">
                                                    <img src={userImage} className="user-img" alt="user" />
                                                </div>
                                                <div className="notification-card-content">
                                                    <div className="notification-card-title-row">
                                                        <h5>
                                                        Factory order awaise approval
                                                        </h5>
                                                        <span className="notification-card-action"></span>
                                                    </div>
                                                    <div className="notification-message">
                                                        <span className="approval-reference">{referenceNo}</span> for {projectName} needs your approval
                                                    </div>
                                                    <p className="approval-notification-meta">
                                                     {formatDate(approval?.requestedAt) || "-"}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {!loading && pendingApprovals?.length > 0 && (
                            <div className="notification-footer">
                                <button type="button" className="view-all-btn" onClick={handleViewAll}>
                                    View All
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                
            </div>
        </div>
    );
};

export default FactoryApprovalNotification;
